import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import type React from 'react';
import { useState } from 'react';

// Ported from the Next app's components/simple-select.tsx (a thin wrapper over
// the Radix select used by the dashboard toolbar).
interface SimpleSelectProps<T extends string> {
  options: { value: T; label: React.ReactNode; group?: string }[];
  onValueChange?: (value: T) => void;
  value?: T;
  defaultValue?: T | number | T[] | readonly string[] | undefined;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  className?: string;
  prefix?: string;
  id?: string;
  form?: string;
  resetLabel?: string;
  name?: string;
}

export function SimpleSelect<T extends string>({
  value,
  onValueChange,
  defaultValue,
  className,
  options,
  placeholder,
  required,
  disabled,
  ref,
  prefix,
  id,
  form,
  resetLabel,
  name,
}: SimpleSelectProps<T>) {
  const [innerValue, setInnerValue] = useState<T | undefined>(undefined);
  const isGrouped = options.some((e) => e.group);
  const groups = options
    .map((e) => e.group)
    .filter((e, i, arr) => arr.indexOf(e) === i);

  const innerOnValueChange = (v: string) => {
    if (v === '--RESET--') {
      if (onValueChange) {
        onValueChange('' as T);
      } else {
        setInnerValue('' as T);
      }
    } else {
      if (onValueChange) {
        onValueChange(v as T);
      } else {
        setInnerValue(v as T);
      }
    }
  };

  return (
    <Select
      value={value ?? innerValue}
      onValueChange={innerOnValueChange}
      defaultValue={defaultValue?.toString()}
      required={required}
      disabled={disabled}
      name={name}
    >
      <SelectTrigger className={className} ref={ref} id={id} form={form}>
        <span>
          {prefix && (
            <span className="text-muted-foreground">{`${prefix}: `}</span>
          )}
          <SelectValue placeholder={placeholder} />
        </span>
      </SelectTrigger>
      <SelectContent>
        {resetLabel && <SelectItem value="--RESET--">{resetLabel}</SelectItem>}
        {isGrouped
          ? groups.map((group) => (
              <SelectGroup key={group ?? 'undefined'}>
                <SelectLabel>{group}</SelectLabel>
                {options
                  .filter((e) => e.group === group)
                  .map(({ value, label }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
              </SelectGroup>
            ))
          : options.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  );
}
